import {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import ModalWithoutScroll from "../ModalWithoutScroll";
import Button from "../Button";

import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import settings from "../../../settings";

import {
    getTemplateSelector,
    getFilteredTemplates,
    getFilteredTemplatesLoading,
    getFilteredTemplatesError
} from "../../../redux/templates/templates-selectors";
import {fetchTemplatesByType} from "../../../redux/templates/templates-operations";
import {closeTemplateSelector} from "../../../redux/templates";

import styles from "./template-selector.module.scss";

const preserveEmptyTags = (html) => {
    return html.replace(/<p><\/p>/g, '<p></p>');
};

const TemplateSelector = () => {
    const dispatch = useDispatch();
    const templateSelector = useSelector(getTemplateSelector);
    const templates = useSelector(getFilteredTemplates);
    const loading = useSelector(getFilteredTemplatesLoading);
    const error = useSelector(getFilteredTemplatesError);

    const [activeTemplate, setActiveTemplate] = useState(null);
    const [formData, setFormData] = useState({
        subject: "",
        cc: "",
        message: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [callbackError, setCallbackError] = useState(null);
    const [editorState, setEditorState] = useState(() => {
        const blocksFromHTML = htmlToDraft('<p></p>');
        const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap
        );
        return EditorState.createWithContent(contentState);
    });

    useEffect(() => {
        if (templateSelector.isOpen && templateSelector.templateType) {
            dispatch(fetchTemplatesByType(templateSelector.templateType));
            setActiveTemplate(null);
            setFormData({
                subject: "",
                cc: "",
                message: ""
            });
            setCallbackError(null);
            const blocksFromHTML = htmlToDraft('<p></p>');
            const contentState = ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap
            );
            setEditorState(EditorState.createWithContent(contentState));
        }
    }, [dispatch, templateSelector.isOpen, templateSelector.templateType]);

    useEffect(() => {
        if (!loading && templates.length === 1 && !activeTemplate) {
            const template = templates[0];
            setActiveTemplate(template);
            setFormData({
                subject: template.title || "",
                cc: "",
                message: template.message || ""
            });
            setEditorState(getEditorContent(template.message || ''));
        }
    }, [loading, templates, activeTemplate]);

    const getEditorContent = (content) => {
        if (!content) {
            const blocksFromHTML = htmlToDraft('<p></p>');
            const contentState = ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap
            );
            return EditorState.createWithContent(contentState);
        }
        const blocksFromHTML = htmlToDraft(content);
        const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap
        );
        return EditorState.createWithContent(contentState);
    };

    const handleSelectTemplate = (template) => {
        setActiveTemplate(template);
        setFormData({
            subject: template.title || "",
            cc: "",
            message: template.message || ""
        });
        setCallbackError(null);
        setEditorState(getEditorContent(template.message || ''));
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const onEditorStateChange = (newState) => {
        setEditorState(newState);
    };

    const handleContinue = async () => {
        if (templateSelector.callback && activeTemplate) {
            try {
                setIsSubmitting(true);
                setCallbackError(null);
                const rawContentState = convertToRaw(editorState.getCurrentContent());
                let messageHtml = draftToHtml(rawContentState);
                messageHtml = preserveEmptyTags(messageHtml);
                await templateSelector.callback({
                    ...activeTemplate,
                    subject: formData.subject,
                    cc: formData.cc,
                    message: messageHtml
                });
                dispatch(closeTemplateSelector());
            } catch (error) {
                console.error("Error executing callback:", error);
                const errorMessage = error?.response?.data?.message || error?.message || "An error occurred while processing your request";
                setCallbackError(errorMessage);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleBack = () => {
        setActiveTemplate(null);
        setFormData({
            subject: "",
            cc: "",
            message: ""
        });
        const blocksFromHTML = htmlToDraft('<p></p>');
        const contentState = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap
        );
        setEditorState(EditorState.createWithContent(contentState));
    };

    const handleClose = () => {
        dispatch(closeTemplateSelector());
    };

    const getHintText = () => {
        if (templateSelector.templateType === settings.EMAIL_TEMPLATES.enrModification) {
            return "You can use [model_name], [user_full_name] and [enr_data] to customize the message";
        }
        return "You can use [model_name] and [user_full_name] to customize the message";
    };

    if (!templateSelector.isOpen) {
        return null;
    }

    return (
        <ModalWithoutScroll title={activeTemplate ? "Configure Template" : "Select Template"} close={handleClose}>
            <div className={styles.container}>
                {activeTemplate ? (
                    <form className={styles.form} onSubmit={(e) => { e.preventDefault(); handleContinue(); }}>
                        <div className={styles.formGroup}>
                            <label htmlFor="subject">Subject</label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="cc">CC</label>
                            <input
                                type="text"
                                id="cc"
                                name="cc"
                                value={formData.cc}
                                onChange={handleInputChange}
                                placeholder="Enter email addresses separated by commas"
                            />
                            <div className={styles.hint}>
                                You can enter multiple email addresses separated by commas to send to multiple recipients
                            </div>
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="message">Message</label>
                            <Editor
                                wrapperClassName={styles.editorWrapper}
                                editorClassName={styles.editor}
                                editorState={editorState}
                                onEditorStateChange={onEditorStateChange}
                            />
                            <div className={styles.hint}>
                                {getHintText()}
                            </div>
                        </div>
                        {callbackError && (
                            <div className={styles.callbackError}>
                                {callbackError}
                            </div>
                        )}
                        <div className={styles.formActions}>
                            {
                                templates.length > 1 && (
                                    <Button onClick={handleBack} active={true}>
                                        Back
                                    </Button>
                                )
                            }
                            
                            <Button type="submit" active={true} disabled={isSubmitting}>
                                {isSubmitting ? "Loading..." : (templateSelector.continueLabel || "Continue")}
                            </Button>
                        </div>
                    </form>
                ) : (
                    <>
                        {loading ? (
                            <div className={styles.loading}>Loading templates...</div>
                        ) : error ? (
                            <div className={styles.error}>Error: {error.message || "Failed to load templates"}</div>
                        ) : templates.length === 0 ? (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyStateTitle}>No template found</div>
                                <div className={styles.emptyStateMessage}>
                                    Please go to Email Templates page to add a template for this type.
                                </div>
                            </div>
                        ) : (
                            <div className={styles.templatesList}>
                                {templates.map((template) => (
                                    <div
                                        key={template._id}
                                        className={styles.templateItem}
                                        onClick={() => handleSelectTemplate(template)}
                                    >
                                        <div className={styles.templateTitle}>{template.title}</div>
                                        {template.message && (
                                            <div
                                                className={styles.templatePreview}
                                                dangerouslySetInnerHTML={{
                                                    __html: template.message.substring(0, 100) + "..."
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </ModalWithoutScroll>
    );
};

export default TemplateSelector;

